import { AppUser } from "../interfaces/IUsers";
import { findUserById, updateUser as updateUserInFirestore } from "../utils/firestore";
import {
  useCallback,
  useEffect,
  useRef,
  useState
  } from "react";

export const useUser = (userId: string) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Wspólna funkcja pobierająca użytkownika i ustawiająca stan
  const fetchUser = useCallback(async (id: string): Promise<AppUser | null> => {
    if (!id) return null;

    // Wyczyść poprzedni timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }

    // Pokaż loading po krótkim opóźnieniu (debouncing)
    loadingTimeoutRef.current = setTimeout(() => {
      setIsLoading(true);
    }, 100);

    try {
      const data = await findUserById(id);
      setUser(data);
      return data;
    } catch (error) {
      console.error("Error fetching user:", error);
      return null;
    } finally {
      // Wyczyść timeout i ukryj loading
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Wyczyść stan przy zmianie userId, żeby nie migały stare dane
    setUser(null);
    void fetchUser(userId);

    // Cleanup timeout przy unmount
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [userId, fetchUser]);

  const updateUser = async (updated: AppUser): Promise<{ success: boolean; error?: string }> => {
    try {
      await updateUserInFirestore(updated);
      setUser(updated);
      return { success: true };
    } catch (error) {
      console.error("Błąd podczas aktualizacji użytkownika:", error);
      const errorMessage = error instanceof Error ? error.message : "Nie udało się zaktualizować danych użytkownika";
      return { success: false, error: errorMessage };
    }
  };

  const refreshUser = async () => {
    if (!userId) return;
    await fetchUser(userId);
  };

  return { user, isLoading, updateUser, refreshUser };
};
